import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import { useId, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { formatClockAndAge } from "@/components/Dispatch/format";
import type { DriverRouteSummary } from "@/types/driverRoute";
import { zinc } from "@/ui/tokens";
import { ContextCaption, ContextField, ContextText } from "./ContextBlock";
import { NO_VALUE } from "./contextFormat";
import { formatFactors } from "./routeFormat";

/**
 * When the route frees the driver for their next load, as the clock time with how far off it is, and on what basis —
 * Storyboard's projection while the route runs, the actual moment once it is done. Says so when the driver is on no
 * route or the source built no forecast.
 *
 * Under it, the Factors: the HOS rests the forecast projects before the PTA, or that none is needed, wherever the
 * forecast walked the driver's hours of service (see {@link formatFactors}). They open from one line, closed by
 * default, so the section stays short until the dispatcher asks why the PTA is where it is.
 */
export default function PtaField({ route, now }: { route: DriverRouteSummary | null; now: number }) {
  const time = route ? formatClockAndAge(route.pta, now) : null;
  const factors = route ? formatFactors(route) : null;
  return (
    <ContextField
      icon={<EventAvailableOutlinedIcon sx={{ fontSize: 18 }} />}
      label="PTA"
      note="Planned Time of Availability"
      testId={TestIds.dispatch.contextPta}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <ContextText variant="hero" component="span">
          {time?.clock ?? NO_VALUE}
        </ContextText>
        {time?.age && (
          <ContextText variant="subtext" component="span">
            {time.age}
          </ContextText>
        )}
      </Box>
      <ContextText variant="subtext">{footnoteFor(route, time?.full ?? null)}</ContextText>
      {route && factors && <PtaFactors key={route.routeId} lines={factors} />}
    </ContextField>
  );
}

function footnoteFor(route: DriverRouteSummary | null, full: string | null): string {
  if (!route) return "No current route";
  if (!full) return "No projection for this source";
  return `${full} · ${route.ptaIsActual ? "actual" : "projected"}`;
}

/**
 * The Factors as a disclosure: closed, one line of the caption and the first factor cut to the field's width; open,
 * every factor a line each, pushing the sections under it down rather than covering them.
 *
 * Keyed by route in the caller, so a dispatcher moving to another driver finds it closed again.
 */
function PtaFactors({ lines }: { lines: readonly string[] }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <Box data-sb={TestIds.dispatch.contextPtaFactors} sx={{ mt: 0.5 }}>
      <ButtonBase
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        sx={{
          width: "calc(100% + 8px)",
          mx: -0.5,
          px: 0.5,
          py: 0.25,
          gap: 0.75,
          justifyContent: "flex-start",
          textAlign: "left",
          borderRadius: 0.75,
          "&:hover": { bgcolor: zinc[800] },
        }}
      >
        <ContextCaption component="span">Factors</ContextCaption>
        {!open && (
          <ContextText
            variant="subtext"
            component="span"
            sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {lines[0]}
          </ContextText>
        )}
        <ExpandMoreIcon
          sx={{
            ml: "auto",
            flex: "none",
            fontSize: 18,
            color: zinc[400],
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms",
          }}
        />
      </ButtonBase>
      <Collapse in={open} id={bodyId}>
        {lines.map((line) => (
          <ContextText key={line} variant="subtext">
            {line}
          </ContextText>
        ))}
      </Collapse>
    </Box>
  );
}
